import React, { forwardRef, useCallback, useMemo, useRef } from 'react';
import { clsx } from 'clsx';
import { mergeRefs } from '@reactuses/core';
import { isNotSet, isSet } from '@/utils';
import useInteractiveOutsideHandler from '@/hooks/useInteractiveOutsideHandler';
import { useKeyboardStore } from '@/store/keyboardStore';
import Flexbox from '@/components/common/Flexbox';
export const Input = forwardRef<HTMLInputElement | HTMLTextAreaElement, ((ElementProps<'input'> & {
  type?: undefined | 'input';
}) | (ElementProps<'textarea'> & {
  type: 'textarea';
})) & {
  leftSlot?: React.ReactElement;
  rightSlot?: React.ReactElement;
  inputWrapperClassname?: string;
  outerWrapperClassname?: string;
  error?: unknown;
  withoutBorder?: boolean;
  disabledPadding?: boolean;
  digitOnly?: boolean;
  serializer?: (value: string) => string;
  testIdPrefix?: string;
  'data-test-id'?: string;
}>(({
  className,
  inputWrapperClassname,
  outerWrapperClassname,
  leftSlot,
  rightSlot,
  error,
  withoutBorder,
  disabledPadding,
  digitOnly,
  onChange,
  serializer,
  maxLength,
  testIdPrefix = '',
  onFocus,
  onBlur,
  ...props
}, ref) => {
  const basicClass = useRef(clsx('w-full', {
    'px-4 py-3': !disabledPadding
  }));
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const isCurrentFocusing = useRef(false);
  const setKeyboardOpen = useKeyboardStore(state => state.setKeyboardOpen);
  useInteractiveOutsideHandler({
    element: containerRef.current,
    callback: () => {
      if (isCurrentFocusing.current) {
        setTimeout(() => inputRef.current?.blur(), 300);
      }
    }
  });
  const onInnerChange = useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (serializer) {
      event.target.value = serializer(event.target.value);
    } else if (digitOnly) {
      event.target.value = event.target.value.replace(/\D/g, '');
    }
    if (isSet(maxLength)) {
      event.target.value = event.target.value.slice(0, maxLength);
    }
    // @ts-ignore
    onChange?.(event);
  }, [digitOnly, maxLength, onChange]);
  const testId = useMemo(() => {
    if (props['data-test-id']) {
      return props['data-test-id'];
    } else {
      if (props.name || testIdPrefix) return `${testIdPrefix ? testIdPrefix : ''}${props.name ? props.name : ''}-input`;
    }
  }, [testIdPrefix, props.name, props['data-test-id']]);
  const focusHandler = useCallback<React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>>(event => {
    setKeyboardOpen(true);
    isCurrentFocusing.current = true;
    // 延到下一個 frame 再捲動，避開和系統鍵盤彈出造成的 viewport resize 同時發生，
    // 否則 iOS Safari 有機率讓 textarea 內部渲染留下錯位殘留（多一行空白）。
    requestAnimationFrame(() => {
      containerRef.current?.scrollIntoView({
        block: 'center'
      });
    });
    onFocus?.(event as any);
  }, [onFocus]);
  const blurHandler = useCallback<React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>>(event => {
    setKeyboardOpen(false);
    isCurrentFocusing.current = false;
    onBlur?.(event as any);
  }, [onFocus]);
  return <Flexbox align={'center'} ref={containerRef} className={clsx('rounded-m overflow-hidden', props.disabled ? ['bg-grey6 text-grey4'] : ['bg-white'], 'text-grey1 placeholder:text-grey3', {
    'border border-solid border-red': isSet(error),
    'border border-solid border-grey5 focus-within:border-blue focus-within:shadow-[0px_0px_0px_3px_rgba(89,_123,_212,_0.10)]': !withoutBorder && isNotSet(error)
  }, outerWrapperClassname)}>
        {leftSlot}
        <div className={clsx('flex-1 h-full', inputWrapperClassname)}>
          {props.type === 'textarea' ? <textarea data-test-id={testId} ref={mergeRefs(ref as React.ForwardedRef<HTMLTextAreaElement>, inputRef)} {...props} onChange={onInnerChange} className={clsx(basicClass.current, className, 'resize-none', 'h-full')} onFocus={focusHandler} onBlur={blurHandler} /> : <input data-test-id={testId} ref={mergeRefs(ref as React.ForwardedRef<HTMLInputElement>, inputRef)} {...props} onChange={onInnerChange} className={clsx(basicClass.current, className)} onFocus={focusHandler} onBlur={blurHandler} />}
        </div>
        {rightSlot}
      </Flexbox>;
});
export const CreditCardInput = forwardRef<HTMLInputElement, ElementProps<'input'>>(({
  className,
  onChange,
  onFocus,
  onBlur,
  ...props
}, ref) => {
  const [touched, setTouched] = React.useState(false);
  const basicClass = useRef(clsx('w-full p-[0.875rem]'));
  const setKeyboardOpen = useKeyboardStore(state => state.setKeyboardOpen);
  const onInnerChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(event => {
    const value = event.target.value.replace(/\D/g, '');
    // Chunk the value into groups of four.
    const chunks = value.match(/.{1,4}/g);
    // If there's a chunk, join them with a space.
    event.target.value = chunks ? chunks.join(' ') : value;
    onChange?.(event);
  }, []);
  const onTouched = useCallback(() => {
    setTouched(true);
  }, []);
  const isError = useMemo(() => (props.value as string ?? '')?.length !== 19 && touched, [props.value, touched]);
  const focusHandler = useCallback<React.FocusEventHandler<HTMLInputElement>>(event => {
    setKeyboardOpen(true);
    onTouched();
    onFocus?.(event);
  }, [onFocus, onTouched]);
  const blurHandler = useCallback<React.FocusEventHandler<HTMLInputElement>>(event => {
    setKeyboardOpen(false);
    onBlur?.(event);
  }, [onFocus]);
  return <Flexbox align={'center'} className={clsx('rounded-m bg-white overflow-hidden', 'text-grey1 placeholder:text-grey3', {
    'border border-solid border-red': isError,
    'border border-solid border-grey5 focus-within:border-blue focus-within:shadow-[0px_0px_0px_3px_rgba(89,_123,_212,_0.10)]': !isError
  })}>
      <input ref={ref as React.ForwardedRef<HTMLInputElement>} {...props} onChange={onInnerChange} className={clsx(basicClass.current, className)} onFocus={focusHandler} onBlur={blurHandler} />
    </Flexbox>;
});